import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import ReportSections from "@/components/ReportSections";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Plus, CheckCircle2 } from "lucide-react";

const fmtEur = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(n || 0) + " €";

export default function SimulationResult() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sim, setSim] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/simulations/${id}`).then((r) => setSim(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#0B0E14]"><Navbar />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center">
          <p className="text-muted-foreground mb-6">404</p>
          <Button onClick={() => navigate("/dashboard")} className="bg-primary text-white rounded-lg">{t("result.back_dash")}</Button>
        </main>
      </div>
    );
  }

  if (!sim) {
    return (
      <div className="min-h-screen bg-[#0B0E14]"><Navbar />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 py-16 flex justify-center">
          <div className="h-10 w-10 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0E14]">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <button onClick={() => navigate("/dashboard")} data-testid="result-back-btn" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4" /> {t("result.back_dash")}
        </button>

        <div className="rounded-xl bg-[#151A22] border border-border p-7 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight" data-testid="result-title">{sim.title}</h1>
              <p className="text-sm text-muted-foreground mt-1 font-mono-fin">{t("common.created")} {new Date(sim.created_at).toLocaleString()}</p>
              <p className="inline-flex items-center gap-1.5 text-xs text-[#10B981] mt-3"><CheckCircle2 className="h-3.5 w-3.5" /> {t("result.saved")}</p>
            </div>
            <div className="flex flex-wrap gap-2 font-mono-fin text-sm">
              {sim.revenue ? <span className="rounded-lg border border-border bg-[#0B0E14] px-3 py-1.5">CA {fmtEur(sim.revenue)}</span> : null}
              {sim.ebitda ? <span className="rounded-lg border border-border bg-[#0B0E14] px-3 py-1.5">EBITDA {fmtEur(sim.ebitda)}</span> : null}
              {sim.contested_amount ? <span className="rounded-lg border border-[#EAB308]/40 bg-[#EAB308]/10 text-[#EAB308] px-3 py-1.5">{fmtEur(sim.contested_amount)}</span> : null}
            </div>
          </div>
        </div>

        <ReportSections sections={sim.sections} />

        <div className="flex flex-col sm:flex-row gap-3 mt-8">
          <Button onClick={() => navigate("/new")} data-testid="result-new-btn" className="bg-primary text-white hover:bg-[#6D28D9] rounded-lg">
            <Plus className="h-4 w-4 mr-2" /> {t("result.new")}
          </Button>
          <Button variant="outline" onClick={() => navigate("/history")} className="bg-transparent border-border text-foreground hover:bg-[#1E2532] hover:text-white rounded-lg">
            {t("nav.history")}
          </Button>
        </div>
      </main>
    </div>
  );
}
